/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { Button, Spinner } from '../../../shared/components';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { OperationTaskForm } from '../../../services/types/OperationTask';

interface Props {
  handleSubmit: (values: OperationTaskForm, resetFormCallback: () => void) => void;
  handleCancel: () => void;
  formState?: OperationTaskForm;
  isSubmitting: boolean;
}

const taskFormrSchema = Yup.object().shape({
  title: Yup.string().required('required'),
  dueDate: Yup.string().required('required'),
  status: Yup.string().required('required'),
});

const TaskForm = (props: Props) => {
  const { handleSubmit, handleCancel, formState, isSubmitting } = props;
  const { t } = useTranslation();
  const formInit: OperationTaskForm = {
    title: formState ? formState.title : '',
    description: formState ? formState.description : '',
    dueDate: formState ? formState.dueDate : null,
    status: formState ? formState.status : '',
    isOwner: formState ? formState.isOwner : true,
  };

  return (
    <Formik
      initialValues={formInit}
      validationSchema={taskFormrSchema}
      validateOnMount
      onSubmit={(values, { resetForm }) => {
        const payload = { ...values } as OperationTaskForm;
        handleSubmit(payload, resetForm);
      }}
      onReset={() => {
        // Formik's resetForm() already invokes this onReset handler itself
        // (see formik/dist/formik.cjs.development.js) - calling resetForm()
        // again from in here re-enters it and recurses until the call stack
        // overflows. handleReset (bound to the form's native reset event)
        // already calls resetForm() before this runs, so nothing here needs
        // to reset the form again.
        handleCancel();
      }}
    >
      {(formik) => (
        <form className="form" onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
          <div className="modal__body">
            <div className="task-modal modal-dialog--custom-filter">
              <div className="form-row">
                <div className="col-sm-12">
                  <label className="col-form-label">{t('titleTask')}</label>
                  <Field
                    id="title"
                    name="title"
                    placeholder="Ex. Complete Toolkit"
                    maxLength={100}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="col-sm-12">
                  <label className="col-form-label">{t('descriptionOptional')}</label>
                  <Field as="textarea" id="description" name="description" maxLength={500} />
                </div>
              </div>
              <div className="form-row">
                <div className="col-sm-6">
                  <label className="col-form-label">{t('dueDate')}</label>
                  <div className="input-group input-group-date-picker-wrapper">
                    <div className="form__form-group-field form__date-picker-wrapper">
                      <DatePicker
                        id="dueDate"
                        name="dueDate"
                        selected={formik.values.dueDate}
                        onChange={(date: Date | null) => formik.setFieldValue('dueDate', date)}
                        dateFormat="MM/dd/yyy"
                        className="form-control-date-picker"
                        autoComplete="off"
                        placeholderText="MM/DD/YYYY"
                        minDate={new Date()}
                      />
                      <div className="form__form-group-icon">
                        <FontAwesomeIcon icon={faCalendar} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6">
                  <label className="col-form-label">{t('status')}</label>
                  <div className="select-wrapper">
                    <Field as="select" id="status" name="status">
                      <option value="">{t('selectStatus')}</option>
                      <option value="NOT_STARTED">{t('taskStatus.NOT_STARTED')}</option>
                      <option value="IN_PROGRESS">{t('taskStatus.IN_PROGRESS')}</option>
                      <option value="FINISHED">{t('taskStatus.FINISHED')}</option>
                    </Field>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal__footer modal__foote--custom-filter">
            <hr />
            <Spinner loading={isSubmitting} />
            <Button
              key="btnCancel"
              className="btn-tertiary"
              text={t('cancel')}
              type="reset"
              disabled={isSubmitting}
            />
            <Button
              key="btnSave"
              className="btn-primary"
              text={t(formState ? 'updateTask' : 'createTask')}
              type="submit"
              disabled={isSubmitting || !formik.isValid}
            />
          </div>
        </form>
      )}
    </Formik>
  );
};

export default TaskForm;
